import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { NgxSmartModalService } from 'ngx-smart-modal';
import { AudioStory, AudioStoryState, cloneAudioStory } from '@purplefront/audio-story/data-access';
import { EditorState, setPlyrAudioSource, setPlyrVideoSource, Voice } from '@purplefront/editor/data-access';
import { Store } from '@ngrx/store';
import { environment, Langs, TrackingService, WINDOW } from '@purplefront/shared';
import { Router } from '@angular/router';
import { AppRouterFacadeService } from '@purplefront/app-router/data-access';
import { EditorService } from '../../../../../../editor/data-access/src/lib/services';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';

@Component({
  selector: 'app-audio-story',
  templateUrl: './audio-story.component.html',
  styleUrls: ['./audio-story.component.scss']
})
export class AudioStoryComponent implements OnInit, OnDestroy {
  readonly baseURL = environment.dailyUpUrl;
  @Input() audioContents: AudioStory[];
  @Input() contentLang: Langs;
  @Input() interfaceLang: Langs;
  @Input() lastPage: any;
  @Input() totalAudioStories: any;
  @Input() voices: Voice[];
  @Output() getAudioStories$ = new EventEmitter();
  @Output() editAudioContent$ = new EventEmitter();
  @Output() updateAudioStoryName$ = new EventEmitter();
  @Output() deleteAudioContent$ = new EventEmitter();

  private _ngUnsubscribe$ = new Subject<void>();
  _success = new Subject<any>();
  timer;

  currentPage = 1;
  isDrawerToggled = {};
  isActionDropdownToggled = {};
  shouldNotTriggerDrawer = [
    'click-edit',
    'dropdownSpan',
    'dropdownIcon',
    'clone-link',
    'clone-span',
    'clone-list',
    'delete-span',
    'delete-link',
    'delete-list'
  ];
  public currentUrl: string;

  modalId = 'audioStoryModal';

  constructor(
    private _ngxSmartModalService: NgxSmartModalService,
    private _editorStore: Store<EditorState>,
    public _router: Router,
    private _appRouterFacade: AppRouterFacadeService,
    private _audioStoryStore: Store<AudioStoryState>,
    private _editorService: EditorService,
    private _trackingService: TrackingService,
    private _clipboard: Clipboard,
    @Inject(WINDOW) private window: Window
  ) {}

  ngOnInit(): void {
    this.getActiveUrl();
    this.scrollToTop();
  }

  scrollToTop(): void {
    this.window.scroll(0, 0);
  }

  goToMyAudioContents(): void {
    this._appRouterFacade.goToMyAudioContents(this.contentLang);
  }

  getActiveUrl(): void {
    this.currentUrl = this._router.url.substring(0, this._router.url.lastIndexOf('/'));
  }

  loadMoreAudioStories(): void {
    this.trackEvent('creations-see-more-creation');
    this.currentPage = this.currentPage + 1;
    this.getAudioStories$.emit(this.currentPage);
  }

  deleteAudioContent(audioContent): void {
    this.trackEvent('creations-delete-creation');
    this.deleteAudioContent$.emit(audioContent);
  }

  editAudioContent(audioContent): void {
    this.trackEvent('creations-edit-creation');
    this.editAudioContent$.emit(audioContent);
  }

  cloneAudioStory(audioContent): void {
    this.trackEvent('creations-clone-creation');
    this.toggleActionDropdown(audioContent.id);
    const clone = { ...audioContent };
    this.interfaceLang === 'en' ? (clone.name = clone.name + ' (Copy)') : (clone.name = clone.name + ' (Copie)');

    this._audioStoryStore.dispatch(cloneAudioStory({ payload: this._editorService.mapAudioStoryToFormValues(clone) }));
  }

  openAudioStoryModal(url, format): void {
    this.trackEvent('creations-clone-creation', 'creation', { format });
    this.setVideoPlayerSource(url);
    this._ngxSmartModalService.getModal(this.modalId).open();
  }

  toggleDrawer(id: string | number, event): void {
    if (this.shouldNotTriggerDrawer.includes(event.target.id)) {
      return;
    }
    this.isDrawerToggled[id] = !this.isDrawerToggled[id];
  }

  toggleActionDropdown(id): void {
    this.isActionDropdownToggled[id] = !this.isActionDropdownToggled[id];
  }

  hasAudioAssets(audioStory: AudioStory): boolean {
    return !!audioStory.assets.audioStream || !!audioStory.assets.audioAttachment;
  }

  hasMobileVideoAssets(audioStory: AudioStory): boolean {
    return !!audioStory.assets.videoMobileStream || !!audioStory.assets.videoMobileAttachment;
  }

  hasSquareVideoAssets(audioStory: AudioStory): boolean {
    return !!audioStory.assets.videoStream || !!audioStory.assets.videoAttachment;
  }

  hasLandscapeVideoAssets(audioStory: AudioStory) {
    return !!audioStory.assets.videoLandscapeStream || !!audioStory.assets.videoLandscapeAttachment;
  }

  hasBeenGenerated(audioStory: AudioStory): boolean {
    return (
      !!audioStory.assets.audioStream ||
      !!audioStory.assets.audioAttachment ||
      !!audioStory.assets.videoMobileStream ||
      !!audioStory.assets.videoMobileAttachment ||
      !!audioStory.assets.videoStream ||
      !!audioStory.assets.videoAttachment ||
      !!audioStory.assets.videoLandscapeStream ||
      !!audioStory.assets.videoLandscapeAttachment
    );
  }

  hasImage(audioStory: AudioStory): boolean {
    if (!this.hasItems(audioStory)) {
      return false;
    }
    for (let item of audioStory.items) {
      if (item.image?.length || item.images?.length) {
        return true;
      }
    }
    return false;
  }

  getFirstImageFromItems(audioStory: AudioStory): string | null {
    if (!this.hasItems(audioStory)) {
      return null;
    }
    for (let item of audioStory.items) {
      if (item.images?.length) {
        return item.images[0];
      }
    }
    return null;
  }

  hasItems(audioStory): boolean {
    return audioStory.items && audioStory.items.length;
  }

  setAudioPlayerSource(url, format): void {
    this.trackEvent('all-play-creation', 'creation', { format });
    this._editorStore.dispatch(setPlyrAudioSource({ payload: url }));
  }

  setVideoPlayerSource(url): void {
    this._editorStore.dispatch(setPlyrVideoSource({ payload: url }));
  }

  downloadSource(download: string, format): void {
    this.trackEvent('all-download-creation', 'creation', { format });
    this.window.location.href = download;
  }

  updateAudioStoryName($event, audioStory: AudioStory): void {
    if ($event.value !== audioStory.name) {
      this.trackEvent('creations-rename-creation');
      const _audioStory = { ...audioStory };
      _audioStory.title = $event.value;
      _audioStory.name = $event.value;
      this.updateAudioStoryName$.emit(_audioStory);
    }
  }

  trackEvent(type: string, value: string = null, payload: any = null): void {
    this._trackingService
      .trackEvent({ type: type, value: value, payload: payload })
      .pipe(takeUntil(this._ngUnsubscribe$))
      .subscribe();
  }

  findFirstTextItem(items) {
    for (const item of items) {
      if (item.type === 'text') {
        return item;
      }
    }
  }

  getVoice(id: number): Voice {
    return this._editorService.getVoice(id, this.voices);
  }

  showActionMessage(message, format) {
    this._success.next({ message, format });
    setTimeout(() => {
      this._success.next('');
    }, 3000);
  }

  copyToClipboard(audioStory: AudioStory, formatUrl: string, format: string) {
    const firstTextItem = this.findFirstTextItem(audioStory.items);
    const voice = this.getVoice(firstTextItem.voice_id);
    const audioStoryMainLang = voice.language.substring(0, 2);
    let codeEmbed = null;
    if (format === 'audio') {
      codeEmbed = this.generateAudioEmbedCode(formatUrl, audioStoryMainLang);
    } else {
      codeEmbed = this.generateVideoEmbedCode(formatUrl, audioStoryMainLang, format);
    }

    this._clipboard.copy(codeEmbed);
    this.showActionMessage('COPIED_CODE_EMBED', format);
  }

  generateAudioEmbedCode(formatUrl: string, audioStoryMainLang: string): string {
    return `<iframe width="100%" height="48" src="${this.baseURL}/player?url=${formatUrl}&lang=${audioStoryMainLang}" title="ETX video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
  }

  generateVideoEmbedCode(formatUrl: string, audioStoryMainLang: string, format): string {
    let resolution = {
      height: 0,
      width: 0
    };
    if (format === 'square-video') {
      resolution.width = 500;
      resolution.height = 500;
    } else if (format === 'landscape-video') {
      resolution.width = 704;
      resolution.height = 396;
    } else if (format === 'mobile-video') {
      resolution.height = 640;
      resolution.width = 360;
    }
    return `<iframe width="${resolution.width}" height="${resolution.height}" src="${this.baseURL}/player?url=${formatUrl}&lang=${audioStoryMainLang}" title="ETX video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
  }

  ngOnDestroy(): void {
    clearTimeout(this.timer);
    this._ngUnsubscribe$.next();
    this._ngUnsubscribe$.complete();
  }
}
